<template>
  <div class="whatsapp-connected">
    <v-row class="mb-4">
      <v-col
        v-for="(stat, idx) in statTiles"
        :key="idx"
        cols="12"
        sm="6"
        md="3"
      >
        <v-card rounded="lg" elevation="0" class="pa-4 h-100">
          <p class="text-caption text-medium-emphasis mb-1">
            {{ $t(stat.labelKey) }}
          </p>
          <p class="text-h5 font-weight-bold mb-1">
            —
          </p>
          <p class="text-caption text-medium-emphasis mb-0">
            {{ $t('settings.whatsapp.connected.statsCaption') }}
          </p>
        </v-card>
      </v-col>
    </v-row>

    <v-card rounded="lg" elevation="0" class="pa-6 mb-4">
      <div class="d-flex flex-wrap align-start justify-space-between gap-2 mb-3">
        <div>
          <div class="d-flex flex-wrap align-center gap-2 mb-1">
            <span class="text-h6">{{ displayNumber }}</span>
            <span v-if="verifiedName" class="text-body-1 text-medium-emphasis">· {{ verifiedName }}</span>
            <v-chip color="success" size="small" variant="flat">
              {{ $t('settings.whatsapp.connected.chipActive') }}
            </v-chip>
          </div>
          <p class="text-body-2 mb-1">
            {{ $t('settings.whatsapp.connected.modeLabel') }}:
            <strong>{{ modeLabel }}</strong>
          </p>
          <p class="text-body-2 mb-1">
            {{ $t('settings.whatsapp.connected.qualityLabel') }}:
            <span :class="qualityClass">{{ qualityDisplay }}</span>
          </p>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ $t('settings.whatsapp.connected.connectedSince', { date: connectedDate }) }}
          </p>
        </div>
        <v-btn
          v-if="isStaff"
          variant="text"
          size="small"
          color="primary"
          :loading="notifyLoading"
          @click="onNotifyBot"
        >
          {{ $t('settings.whatsapp.connected.retryBot') }}
        </v-btn>
      </div>
      <p class="text-caption text-medium-emphasis mb-0">
        {{ $t('settings.whatsapp.connected.disconnectHint') }}
      </p>
    </v-card>

    <!-- TODO: re-enable later — Who answers first + opening hours (data kept in whatsapp.settings) -->
    <v-card v-if="false" rounded="lg" elevation="0" class="pa-6 mb-4">
      <h3 class="text-h6 font-weight-bold mb-4">
        {{ $t('settings.whatsapp.connected.whoAnswersTitle') }}
      </h3>

      <v-radio-group v-model="form.answer_first" inline color="red-darken-3" class="mb-4">
        <v-radio :label="$t('settings.whatsapp.connected.answerStaff')" value="staff" />
        <v-radio :label="$t('settings.whatsapp.connected.answerAssistant')" value="assistant" />
      </v-radio-group>

      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="form.wait_minutes"
            :items="waitOptions"
            :label="$t('settings.whatsapp.connected.waitLabel')"
            variant="outlined"
            density="comfortable"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="form.pause_after_staff_hours"
            :items="pauseOptions"
            :label="$t('settings.whatsapp.connected.pauseLabel')"
            variant="outlined"
            density="comfortable"
          />
        </v-col>
      </v-row>

      <p class="text-subtitle-2 font-weight-bold mt-2 mb-2">
        {{ $t('settings.whatsapp.connected.openingHoursTitle') }}
      </p>
      <div
        v-for="row in openingRows"
        :key="row.day"
        class="d-flex flex-wrap align-center gap-2 mb-2"
      >
        <span class="day-label text-body-2">{{ $t(row.labelKey) }}</span>
        <v-switch
          v-model="row.closed"
          :label="$t('settings.whatsapp.connected.closed')"
          color="red-darken-3"
          density="compact"
          hide-details
          class="flex-grow-0"
          :true-value="true"
          :false-value="false"
        />
        <v-text-field
          v-model="row.from"
          :disabled="row.closed"
          :label="$t('settings.whatsapp.connected.from')"
          variant="outlined"
          density="compact"
          hide-details
          class="time-field"
          maxlength="5"
        />
        <v-text-field
          v-model="row.to"
          :disabled="row.closed"
          :label="$t('settings.whatsapp.connected.to')"
          variant="outlined"
          density="compact"
          hide-details
          class="time-field"
          maxlength="5"
        />
      </div>
      <p v-if="settingsError" class="text-error text-caption mt-2 mb-0">
        {{ settingsError }}
      </p>

      <v-btn
        color="red-darken-3"
        variant="flat"
        class="mt-4"
        :disabled="!settingsDirty || settingsSaving"
        :loading="settingsSaving"
        @click="saveSettings"
      >
        {{ $t('settings.whatsapp.connected.saveSettings') }}
      </v-btn>
    </v-card>

    <whatsapp-bot-rules
      :institution-id="institutionId"
      :whatsapp="whatsapp"
      @snackbar="$emit('snackbar', $event)"
      @reload="$emit('reload')"
    />

    <v-card v-if="false" rounded="lg" elevation="0" class="pa-6">
      <div class="d-flex align-center justify-space-between mb-4">
        <h3 class="text-h6 font-weight-bold mb-0">
          {{ $t('settings.whatsapp.connected.privateContactsTitle') }}
        </h3>
        <span
          v-if="contactsDirty"
          class="text-caption text-amber-darken-4"
        >
          {{ $t('settings.whatsapp.connected.unsavedChanges') }}
        </span>
      </div>

      <v-list v-if="localContacts.length" density="compact" class="mb-4 pa-0">
        <v-list-item
          v-for="(c, idx) in localContacts"
          :key="`${c.phone}-${idx}`"
          class="px-0"
        >
          <v-list-item-title>{{ c.name || '—' }}</v-list-item-title>
          <v-list-item-subtitle>+{{ c.phone }}</v-list-item-subtitle>
          <template #append>
            <v-btn icon variant="text" size="small" @click="removeContact(idx)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
      <p v-else class="text-body-2 text-medium-emphasis mb-4">
        {{ $t('settings.whatsapp.connected.noPrivateContacts') }}
      </p>

      <v-btn variant="outlined" color="red-darken-3" @click="addDialog = true">
        {{ $t('settings.whatsapp.connected.addContact') }}
      </v-btn>
      <v-btn
        color="red-darken-3"
        variant="flat"
        class="ml-2"
        :disabled="!contactsDirty || contactsSaving"
        :loading="contactsSaving"
        @click="saveContacts"
      >
        {{ $t('settings.whatsapp.connected.saveContacts') }}
      </v-btn>
    </v-card>

    <v-dialog v-model="addDialog" max-width="440">
      <v-card class="pa-4">
        <v-card-title>{{ $t('settings.whatsapp.connected.addContactTitle') }}</v-card-title>
        <v-text-field
          v-model="newContact.name"
          :label="$t('settings.whatsapp.connected.contactName')"
          variant="outlined"
          maxlength="80"
          class="mt-2"
        />
        <v-text-field
          v-model="newContact.phone"
          :label="$t('settings.whatsapp.connected.contactPhone')"
          variant="outlined"
          :hint="$t('settings.whatsapp.connected.phoneHint')"
          persistent-hint
          :error-messages="addPhoneError ? [addPhoneError] : []"
        />
        <p v-if="normalizedPreview" class="text-caption text-medium-emphasis mt-2">
          {{ $t('settings.whatsapp.connected.normalized') }}: +{{ normalizedPreview }}
        </p>
        <v-card-actions class="px-0">
          <v-spacer />
          <v-btn variant="text" @click="addDialog = false">
            {{ $t('close') }}
          </v-btn>
          <v-btn color="red-darken-3" variant="flat" @click="confirmAddContact">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  updateWhatsappSettings,
  updatePrivateContacts,
  notifyWhatsappBot,
} from '@/api/whatsapp';
import WhatsappBotRules from '@/components/Settings/WhatsApp/WhatsappBotRules.vue';
import { whatsappErrorMessage } from '@/utils/whatsappErrors';

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_DEFS = [
  { day: 1, labelKey: 'settings.whatsapp.connected.days.mon' },
  { day: 2, labelKey: 'settings.whatsapp.connected.days.tue' },
  { day: 3, labelKey: 'settings.whatsapp.connected.days.wed' },
  { day: 4, labelKey: 'settings.whatsapp.connected.days.thu' },
  { day: 5, labelKey: 'settings.whatsapp.connected.days.fri' },
  { day: 6, labelKey: 'settings.whatsapp.connected.days.sat' },
  { day: 7, labelKey: 'settings.whatsapp.connected.days.sun' },
];

function normalizePhoneDigits(raw) {
  if (raw == null) {
    return '';
  }
  return String(raw).replace(/\D/g, '');
}

export default {
  name: 'WhatsappConnected',
  components: { WhatsappBotRules },
  props: {
    whatsapp: {
      type: Object,
      required: true,
    },
    institutionId: {
      type: String,
      required: true,
    },
    isStaff: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['snackbar', 'reload'],
  data() {
    return {
      form: {
        answer_first: 'staff',
        wait_minutes: 3,
        pause_after_staff_hours: 4,
      },
      openingRows: [],
      baselineSettingsJson: '',
      localContacts: [],
      baselineContactsJson: '',
      settingsError: null,
      settingsSaving: false,
      contactsSaving: false,
      notifyLoading: false,
      addDialog: false,
      newContact: { name: '', phone: '' },
      addPhoneError: null,
      waitOptions: [0, 1, 3, 5, 10],
      pauseOptions: [1, 2, 4, 8, 24],
      statTiles: [
        { labelKey: 'settings.whatsapp.connected.statConversations' },
        { labelKey: 'settings.whatsapp.connected.statAssistant' },
        { labelKey: 'settings.whatsapp.connected.statStaff' },
        { labelKey: 'settings.whatsapp.connected.statFreeReplies' },
      ],
    };
  },
  computed: {
    displayNumber() {
      return this.whatsapp.display_phone_number || '—';
    },
    verifiedName() {
      return this.whatsapp.verified_name || '';
    },
    modeLabel() {
      if (this.whatsapp.mode === 'coexistence') {
        return this.$t('settings.whatsapp.connected.modeCoexistence');
      }
      if (this.whatsapp.mode === 'dedicated') {
        return this.$t('settings.whatsapp.connected.modeDedicated');
      }
      return '—';
    },
    qualityDisplay() {
      const q = this.whatsapp.quality_rating;
      if (!q) {
        return '—';
      }
      return String(q).toUpperCase();
    },
    qualityClass() {
      const q = this.qualityDisplay;
      if (q === '—') {
        return 'text-medium-emphasis';
      }
      if (['HIGH', 'GREEN'].includes(q)) {
        return 'text-success font-weight-medium';
      }
      if (['MEDIUM', 'YELLOW'].includes(q)) {
        return 'text-warning font-weight-medium';
      }
      if (['LOW', 'RED'].includes(q)) {
        return 'text-error font-weight-medium';
      }
      return 'text-medium-emphasis';
    },
    connectedDate() {
      if (!this.whatsapp.connected_at) {
        return '—';
      }
      try {
        return new Date(this.whatsapp.connected_at).toLocaleDateString();
      } catch (e) {
        return '—';
      }
    },
    settingsDirty() {
      return this.baselineSettingsJson !== JSON.stringify(this.serializeSettings());
    },
    contactsDirty() {
      return this.baselineContactsJson !== JSON.stringify(this.localContacts);
    },
    normalizedPreview() {
      const digits = normalizePhoneDigits(this.newContact.phone);
      return digits || '';
    },
  },
  watch: {
    whatsapp: {
      deep: true,
      handler() {
        this.hydrateFromWhatsapp();
      },
    },
  },
  mounted() {
    this.hydrateFromWhatsapp();
  },
  methods: {
    hydrateFromWhatsapp() {
      const s = this.whatsapp.settings || {};
      this.form = {
        answer_first: s.answer_first || 'staff',
        wait_minutes: s.wait_minutes != null ? s.wait_minutes : 3,
        pause_after_staff_hours: s.pause_after_staff_hours != null
          ? s.pause_after_staff_hours
          : 4,
      };
      this.openingRows = this.buildOpeningRows(s.opening_hours || []);
      this.baselineSettingsJson = JSON.stringify(this.serializeSettings());

      this.localContacts = (this.whatsapp.private_contacts || []).map((c) => ({
        phone: c.phone,
        name: c.name || '',
      }));
      this.baselineContactsJson = JSON.stringify(this.localContacts);
    },
    buildOpeningRows(hours) {
      const byDay = {};
      hours.forEach((h) => {
        byDay[h.day] = h;
      });
      return DAY_DEFS.map(({ day, labelKey }) => {
        const existing = byDay[day];
        if (existing) {
          return {
            day,
            labelKey,
            closed: false,
            from: existing.from,
            to: existing.to,
          };
        }
        return {
          day,
          labelKey,
          closed: true,
          from: '09:00',
          to: '18:00',
        };
      });
    },
    serializeSettings() {
      const opening_hours = [];
      this.openingRows.forEach((row) => {
        if (!row.closed) {
          opening_hours.push({
            day: row.day,
            from: row.from,
            to: row.to,
          });
        }
      });
      return {
        answer_first: this.form.answer_first,
        wait_minutes: this.form.wait_minutes,
        pause_after_staff_hours: this.form.pause_after_staff_hours,
        opening_hours,
      };
    },
    validateSettings() {
      this.settingsError = null;
      for (let i = 0; i < this.openingRows.length; i += 1) {
        const row = this.openingRows[i];
        if (row.closed) {
          continue;
        }
        if (!TIME_RE.test(row.from) || !TIME_RE.test(row.to)) {
          this.settingsError = this.$t('settings.whatsapp.connected.invalidTime');
          return false;
        }
        if (row.from >= row.to) {
          this.settingsError = this.$t('settings.whatsapp.connected.timeOrder');
          return false;
        }
      }
      return true;
    },
    async saveSettings() {
      if (!this.validateSettings()) {
        return;
      }
      this.settingsSaving = true;
      try {
        const payload = this.serializeSettings();
        await updateWhatsappSettings(this.institutionId, payload);
        this.baselineSettingsJson = JSON.stringify(payload);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.connected.settingsSaved'),
          color: 'success',
        });
        this.$emit('reload');
      } catch (err) {
        this.$emit('snackbar', {
          message: whatsappErrorMessage(err),
          color: 'error',
        });
      } finally {
        this.settingsSaving = false;
      }
    },
    removeContact(idx) {
      this.localContacts.splice(idx, 1);
    },
    confirmAddContact() {
      this.addPhoneError = null;
      const name = (this.newContact.name || '').trim();
      if (name.length > 80) {
        this.addPhoneError = this.$t('settings.whatsapp.connected.nameTooLong');
        return;
      }
      const phoneRaw = (this.newContact.phone || '').trim();
      if (!phoneRaw.startsWith('+') || !/^\+[\d\s-]+$/.test(phoneRaw)) {
        this.addPhoneError = this.$t('settings.whatsapp.connected.phoneInvalid');
        return;
      }
      const digits = normalizePhoneDigits(phoneRaw);
      if (digits.length < 8) {
        this.addPhoneError = this.$t('settings.whatsapp.connected.phoneInvalid');
        return;
      }
      if (this.localContacts.some((c) => c.phone === digits)) {
        this.addPhoneError = this.$t('settings.whatsapp.connected.phoneDuplicate');
        return;
      }
      this.localContacts.push({ name, phone: digits });
      this.newContact = { name: '', phone: '' };
      this.addDialog = false;
    },
    async saveContacts() {
      this.contactsSaving = true;
      try {
        await updatePrivateContacts(this.institutionId, this.localContacts);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.connected.contactsSaved'),
          color: 'success',
        });
        this.$emit('reload');
      } catch (err) {
        this.$emit('snackbar', {
          message: whatsappErrorMessage(err),
          color: 'error',
        });
      } finally {
        this.contactsSaving = false;
      }
    },
    async onNotifyBot() {
      this.notifyLoading = true;
      try {
        const res = await notifyWhatsappBot(this.institutionId);
        if (res.status === 202 || res.status === 200) {
          this.$emit('snackbar', {
            message: this.$t('settings.whatsapp.connected.notifyOk'),
            color: 'success',
          });
        }
        this.$emit('reload');
      } catch (err) {
        this.$emit('snackbar', {
          message: whatsappErrorMessage(err),
          color: 'error',
        });
      } finally {
        this.notifyLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.day-label {
  min-width: 88px;
}
.time-field {
  max-width: 120px;
}
</style>
